<script module>
  import AppLayout from '$lib/layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script lang="ts">
  import { usePage } from "@inertiajs/svelte";
  import { appStore } from '$lib/stores/appStore';

  // 引入模块化组件
  import SubscriptionBannerModal from '$lib/components/dashboard/SubscriptionBannerModal.svelte';
  import SiteHeader from '$lib/components/dashboard/SiteHeader.svelte';
  import KpiTodayPv from '$lib/components/dashboard/KpiTodayPv.svelte';
  import KpiTodayUv from '$lib/components/dashboard/KpiTodayUv.svelte';
  import KpiYesterdayTraffic from '$lib/components/dashboard/KpiYesterdayTraffic.svelte';
  import KpiInquiries from '$lib/components/dashboard/KpiInquiries.svelte';
  import KpiConversionRate from '$lib/components/dashboard/KpiConversionRate.svelte';
  import KpiContentUpdates from '$lib/components/dashboard/KpiContentUpdates.svelte';
  import KpiNodePerformance from '$lib/components/dashboard/KpiNodePerformance.svelte';

  import TrafficTrendChart from '$lib/components/dashboard/TrafficTrendChart.svelte';
  import SpiderTrendChart from '$lib/components/dashboard/SpiderTrendChart.svelte';
  import TopPagesChart, { type TopPageItem } from '$lib/components/dashboard/TopPagesChart.svelte';
  import TrafficSourcesChart, { type TrafficSourceItem } from '$lib/components/dashboard/TrafficSourcesChart.svelte';
  import TopCountriesChart, { type CountryItem } from '$lib/components/dashboard/TopCountriesChart.svelte';

  interface DashboardProps {
    subscription?: {
      plan_name: string;
      plan_key: string;
      is_trial: boolean;
      days_left: number;
      total_trial_days?: number;
      expires_at: string;
      pv_limit: number;
      storage_limit_mb: number;
    };
    stats?: {
      today_pv: number;
      yesterday_pv: number;
      today_uv: number;
      yesterday_uv: number;
      pv_growth_rate: number;
      today_inquiries: number;
      yesterday_inquiries: number;
      total_inquiries: number;
      total_pages: number;
      media_usage_mb: number;
      page_load_ms: number;
      conversion_rate?: number;
      monthly_updates?: number;
    };
    plans?: Array<any>;
    traffic_trends?: Array<{ hour: string; pv: number; uv: number }>;
    seo_trend?: { labels: string[]; spider_visits: number[] };
    top_pages?: TopPageItem[];
    traffic_sources?: TrafficSourceItem[];
    top_countries?: CountryItem[];
  }

  let { 
    subscription = {
      plan_name: '',
      plan_key: 'trial',
      is_trial: true,
      days_left: 7,
      total_trial_days: 14,
      expires_at: '',
      pv_limit: 10000,
      storage_limit_mb: 500
    },
    stats = {
      today_pv: 0,
      yesterday_pv: 0,
      today_uv: 0,
      yesterday_uv: 0,
      pv_growth_rate: 0,
      today_inquiries: 0,
      yesterday_inquiries: 0,
      total_inquiries: 0,
      total_pages: 0,
      media_usage_mb: 0,
      page_load_ms: 45,
      conversion_rate: 2.4,
      monthly_updates: 12,
    },
    traffic_trends = [],
    seo_trend = { labels: [], spider_visits: [] },
    top_pages = [],
    traffic_sources = [],
    top_countries = [],
    plans = []
  }: DashboardProps = $props();

  const page = usePage();

  let sub = $derived((page.props as any).current_subscription);
  let isTrial = $derived(sub?.status === 'trialing');

  let daysLeft = $derived.by(() => {
    const endDateStr = isTrial ? sub?.trial_ends_at : sub?.current_period_ends_at;
    if (!endDateStr) return 0;
    const targetTime = new Date(endDateStr).getTime();
    if (isNaN(targetTime)) return 0;
    return Math.max(0, Math.ceil((targetTime - new Date().getTime()) / (1000 * 60 * 60 * 24)));
  });

  let activeSubscription = $derived({
    plan_name: sub?.plan_name || subscription.plan_name,
    plan_key: sub?.plan_level || subscription.plan_key,
    is_trial: isTrial,
    days_left: daysLeft,
    expires_at: sub?.current_period_ends_at ? new Date(sub.current_period_ends_at).toISOString().split('T')[0] : '',
    pv_limit: sub?.limits?.pv_limit ?? subscription.pv_limit,
    storage_limit_mb: sub?.limits?.storage_mb ?? subscription.storage_limit_mb
  });

  let currentSite = $derived(page.props?.current_site as any);
  let siteRegion = $derived(page.props.site_region as string || 'CHINA');
  const translations = $derived((page.props as any).translations || {});
  const tl = $derived(translations.dashboard || {}); 

  function t(path: string, params: Record<string, any> = {}): string {
    const keys = path.split('.');
    let current: any = page.props?.translations || page.props?.t || {};
    
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return path;
      }
    }
    
    if (typeof current !== 'string') return path;
    return current.replace(/%\{(\w+)\}/g, (_, key) => params[key] !== undefined ? String(params[key]) : `%{${key}}`);
  }

  // 站点在线检测状态
  let siteHealth = $state<'testing' | 'online' | 'offline'>('testing');

  $effect(() => {
    appStore.setTitle(t('dashboard.page_title'));
    checkSiteStatus();
  });

  async function checkSiteStatus() {
    if (!currentSite?.primary_domain) {
      siteHealth = 'offline';
      return;
    }
    siteHealth = 'testing';
    try {
      const res = await fetch('/site/check_health');
      const data = await res.json();
      siteHealth = data.health;
    } catch {
      siteHealth = 'offline';
    }
  }
</script>

<svelte:head>
  <title>{t('dashboard.browser_title')}</title>
</svelte:head>

<div class="bg-background text-foreground w-full p-2 md:p-4 space-y-6">
  <!-- 1. 租户套餐 Banner & 升级/收银台 Modal -->
  <SubscriptionBannerModal 
    {activeSubscription}
    {plans}
    {siteRegion}
    {tl}
    {t}
  />

  <!-- 2. 站点名称 & 域名 & 二维码 Header -->
  
  <SiteHeader 
    {currentSite} 
    {siteHealth} 
    onCheckHealth={checkSiteStatus} 
    {tl} 
    {t} 
  />
  

  <!-- 3. 核心业务 KPI 概览网格 -->
  <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
    <KpiTodayPv todayPv={stats.today_pv} growthRate={stats.pv_growth_rate} {t} />
    <KpiTodayUv todayUv={stats.today_uv} {t} />
    <KpiYesterdayTraffic yesterdayPv={stats.yesterday_pv} yesterdayUv={stats.yesterday_uv} {t} />
    <KpiInquiries type="today" todayInquiries={stats.today_inquiries} totalInquiries={stats.total_inquiries} {t} />
    <KpiInquiries type="yesterday" yesterdayInquiries={stats.yesterday_inquiries} {t} />
    <KpiConversionRate conversionRate={stats.conversion_rate ?? 0} {t} />
    <KpiContentUpdates monthlyUpdates={stats.monthly_updates ?? 0} totalPages={stats.total_pages} {t} />
    <KpiNodePerformance mediaUsageMb={stats.media_usage_mb} storageLimitMb={subscription.storage_limit_mb} pageLoadMs={stats.page_load_ms} {t} />
  </div>

  <!-- 4. 业务图表网格 -->
  <div class="grid gap-6 md:grid-cols-2">
    <TrafficTrendChart trafficTrends={traffic_trends} {t} />
    <SpiderTrendChart seoTrend={seo_trend} {t} />
    <TopPagesChart {top_pages} {t} />
    <TrafficSourcesChart sources={traffic_sources} {t} />
    <TopCountriesChart countries={top_countries} {t} />
  </div>
</div>